import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft2, Cup } from "iconsax-react";

import MobileLayout from "../../components/MobileLayout";
import { Md3Button } from "../../shared/components";
import { useGamification } from "../../hooks/useGamification";
import type { GameWord } from "../../hooks/useGameWords";
import {
  AnswerInput,
  CompleteCard,
  Feedback,
  GamePageRoot,
  GameTitle,
  GameTopBar,
  MediaBox,
  ProgressText,
  ScoreBadge,
} from "./gameStyles";

type Props = { words: GameWord[]; onExit?: () => void };

function maskWord(word: string): { display: string; answer: string } {
  const clean = word.replace(/\d+$/, "").trim();
  if (clean.length <= 2) {
    return { display: `${clean[0] || ""}_`, answer: clean };
  }
  const mid = Math.floor(clean.length / 2);
  const chars = clean.split("");
  chars[mid] = "_";
  if (clean.length > 4) chars[mid + 1] = "_";
  return { display: chars.join(""), answer: clean };
}

const WordCompletionGame: React.FC<Props> = ({ words, onExit }) => {
  const navigate = useNavigate();
  const { addPoints } = useGamification();
  const questions = useMemo(
    () =>
      words
        .filter((w) => w.palavra?.trim().length > 2)
        .map((w) => ({ word: w, ...maskWord(w.palavra) })),
    [words]
  );
  const [index, setIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [value, setValue] = useState("");
  const [checked, setChecked] = useState(false);
  const [done, setDone] = useState(false);

  const current = questions[index];
  const isCorrect =
    value.trim().toLowerCase() === current?.answer.toLowerCase();

  const handleCheck = async () => {
    if (!current || checked) return;
    setChecked(true);
    if (isCorrect) {
      setScore((s) => s + 1);
      await addPoints("QUIZ_DATILOLOGIA");
    }
  };

  const handleNext = () => {
    if (index + 1 >= questions.length) {
      setDone(true);
      return;
    }
    setIndex((i) => i + 1);
    setValue("");
    setChecked(false);
  };

  return (
    <MobileLayout
      headerTitle="Complete a Palavra"
      headerType="private"
      showBannerAd={false}
    >
      <GamePageRoot>
        <GameTopBar>
          <Md3Button
            title="Voltar"
            variant="text"
            onClick={() => (onExit ? onExit() : navigate("/games"))}
            icon={<ArrowLeft2 size={18} />}
          />
          <ScoreBadge>
            <Cup size={16} variant="Bold" />
            {score}
          </ScoreBadge>
        </GameTopBar>

        {done || !current ? (
          <CompleteCard>
            <GameTitle>{done ? "Parabéns!" : "Sem palavras"}</GameTitle>
            {done ? (
              <ProgressText>
                Você acertou {score} de {questions.length}
              </ProgressText>
            ) : null}
            <Md3Button
              title="Voltar aos jogos"
              onClick={() => navigate("/games")}
            />
          </CompleteCard>
        ) : (
          <>
            <ProgressText>
              Palavra {index + 1} de {questions.length}
            </ProgressText>
            <MediaBox>
              {(() => {
                if (current.word.video) {
                  return (
                    // eslint-disable-next-line jsx-a11y/media-has-caption
                    <video
                      src={current.word.video}
                      controls
                      autoPlay
                      loop
                      muted
                      playsInline
                    />
                  );
                }
                if (current.word.image) {
                  return <img src={current.word.image} alt="" />;
                }
                return (
                  <GameTitle style={{ fontSize: 32 }}>
                    {current.display}
                  </GameTitle>
                );
              })()}
            </MediaBox>
            <ProgressText>
              Complete: <strong>{current.display}</strong>
            </ProgressText>
            <AnswerInput
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="Digite a palavra"
              disabled={checked}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleCheck();
              }}
            />
            {!checked ? (
              <Md3Button
                title="Verificar"
                onClick={handleCheck}
                disabled={!value.trim()}
              />
            ) : (
              <>
                <Feedback $ok={isCorrect}>
                  {isCorrect ? "Correto!" : `Resposta: ${current.answer}`}
                </Feedback>
                <Md3Button title="Próxima" onClick={handleNext} />
              </>
            )}
          </>
        )}
      </GamePageRoot>
    </MobileLayout>
  );
};

export default WordCompletionGame;
